import { Controller, useForm } from 'react-hook-form';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  EstateWorkItem,
  useEstateWorkItem_UpdateEstateWorkItemMutation,
  useUser_GetAllTrustateServiceOpsAdminsQuery,
} from 'queries';

import { Button } from 'common/button';
import Modal from 'common/modal';
import Spinner from 'common/spinner';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

const useStyles = makeStyles(() => ({
  container: {
    marginTop: '20px',
    padding: '20px',
    color: '#1C2541',
    fontSize: '14px',
    display: 'flex',
    flexGrow: 1,
    flexDirection: 'column',
    textAlign: 'left',
  },
  button: {
    marginTop: '20px',
  },
  userImage: {
    marginRight: 10,
  },
  form: {
    marginTop: '60px',
  },
}));

type AssignAdminPointPersonModalProps = {
  onClose: () => void;
  estateWorkItem: EstateWorkItem;
  refetchEstateWorkItems: () => void;
};

const AssignAdminPointPersonModal = ({
  onClose,
  estateWorkItem,
  refetchEstateWorkItems,
}: AssignAdminPointPersonModalProps) => {
  const classes = useStyles();

  const { data: allTrustateServiceOpsAdminData, loading: allTrustateServiceOpsAdminDataLoading } =
    useUser_GetAllTrustateServiceOpsAdminsQuery({
      onError: (e) => {
        showAlert({
          message: 'Error getting all Trustate ServiceOps Admins: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const [updateWorkItem, { loading: updateEstateWorkItemExecuting }] =
    useEstateWorkItem_UpdateEstateWorkItemMutation({
      onCompleted: () => {
        showToast({
          message: 'Estate work item updated successfully',
          severity: NotificationSeverity.SUCCESS,
        });
        refetchEstateWorkItems();
        onClose();
      },
      onError: (e) => {
        showAlert({
          message: 'Error updating estate work item: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const { control, handleSubmit } = useForm({
    defaultValues: {
      assignedTsaId: estateWorkItem.assignedTsa?.id,
    },
  });

  const tsaAdmins = allTrustateServiceOpsAdminData?.data || [];

  const onSubmit = (formData: { assignedTsaId: string | undefined }) => {
    updateWorkItem({
      variables: {
        input: {
          id: estateWorkItem.id,
          assignedTsaId: formData.assignedTsaId || null,
        },
      },
    });
  };

  return (
    <Modal
      setShowModal={() => {}}
      onExitClick={onClose}
      showModal={true}
      showActions={false}
      content={
        <div className={classes.container}>
          <Typography variant="h5">Set Admin Point Person</Typography>
          {allTrustateServiceOpsAdminDataLoading && <Spinner />}
          {!allTrustateServiceOpsAdminDataLoading && (
            <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
              <div>
                <Controller
                  name="assignedTsaId"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Autocomplete
                      options={[null, ...tsaAdmins]}
                      value={value ? tsaAdmins.find((tsa) => tsa.id === value) : null}
                      getOptionLabel={(option) => (option ? option.firstName : '-')}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <img
                            className={classes.userImage}
                            loading="lazy"
                            width="20"
                            src={`${
                              option ? (option.avatarUrl ?? '/default_avatar.svg') : undefined
                            }`}
                            srcSet={`${
                              option
                                ? (option.avatarUrl ?? '/default_avatar.svg') + ' 2x'
                                : undefined
                            }`}
                            alt=""
                          />
                          {option ? option.firstName : '-'}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          inputProps={{
                            ...params.inputProps,
                            autoComplete: 'new-password',
                          }}
                        />
                      )}
                      noOptionsText="No TSA found"
                      onChange={(_evt, selected) => onChange(selected?.id ?? null)}
                    />
                  )}
                />
              </div>
              <div className={classes.button}>
                <Button color="primary" type="submit" disabled={updateEstateWorkItemExecuting}>
                  Assign
                </Button>
              </div>
            </form>
          )}
        </div>
      }
      loading={false}
      actionDisabled={false}
      maxWidth="sm"
    />
  );
};

export default AssignAdminPointPersonModal;
